// Resolve a ZenConfig into the shapes the codegen emit functions need
// FEATURE: zen-config resolver replacing adaptToLegacyConfig
import { getTableConfig, type PgColumn, type PgTable } from "drizzle-orm/pg-core";
import type {
  ComputedFieldsConfig,
  DerivedModelsConfig,
  FilteredRelationsConfig,
  ResolvedRelationTargets,
  SearchDefaultsConfig,
  ThroughRelationsConfig,
} from "./codegen-types";
import type { ZenConfig } from "./config";
import type {
  DerivedField,
  DerivedModelDescriptor,
  ManyRelation,
  OneRelation,
  SearchField,
  SearchProfile,
  ThroughRelation,
} from "./dsl/types";

export interface FKRelationInfo {
  targetExportName: string;
  fields: string[];
  references: string[];
  relationName: string | null;
}

export interface ResolvedPolymorphicTarget {
  table: string;
  tableId: string;
}

export interface ResolvedPolymorphic {
  discriminator: string;
  idField: string;
  nullable: boolean;
  targets: ResolvedPolymorphicTarget[];
}

export interface ResolvedConfig {
  defaultWhere: Record<string, unknown> | undefined;
  computedFields: ComputedFieldsConfig;
  derivedModels: DerivedModelsConfig;
  filteredRelations: FilteredRelationsConfig;
  searchDefaults: SearchDefaultsConfig;
  throughRelations: ThroughRelationsConfig;
  relationTargets: ResolvedRelationTargets;
  modelScopes: Record<string, Record<string, unknown>>;
  derivedFields: Record<string, Record<string, DerivedField>>;
  validationSchemas: Record<string, Record<string, unknown>>;
  fkRelations: Record<string, Record<string, FKRelationInfo>>;
  brandedFields: Record<string, Record<string, true>>;
  polymorphics: Record<string, ResolvedPolymorphic>;
}

const COMPUTED_TYPE_MAP: Record<string, { type: string; tsType: string }> = {
  json: { type: "Json", tsType: "JsonValue" },
  string: { type: "String", tsType: "string" },
  text: { type: "String", tsType: "string" },
  int: { type: "Int", tsType: "number" },
  float: { type: "Float", tsType: "number" },
  bool: { type: "Boolean", tsType: "boolean" },
  boolean: { type: "Boolean", tsType: "boolean" },
  dateTime: { type: "DateTime", tsType: "Date" },
};

function resolveColumn(
  tables: Record<string, PgTable>,
  column: PgColumn,
): { table: PgTable; jsKey: string } | undefined {
  for (const table of Object.values(tables)) {
    for (const key of Object.keys(table as object)) {
      if ((table as any)[key] === column) return { table, jsKey: key };
    }
  }
  const sqlName = column.name;
  for (const table of Object.values(tables)) {
    for (const key of Object.keys(table as object)) {
      const col = (table as any)[key];
      if (col && typeof col === "object" && "name" in col && col.name === sqlName) {
        return { table, jsKey: key };
      }
    }
  }
  return undefined;
}

function findExportNameForTable(tables: Record<string, PgTable>, target: PgTable): string {
  for (const [exportName, table] of Object.entries(tables)) {
    if (table === target) return exportName;
  }
  const targetSqlName = getTableConfig(target).name;
  for (const [exportName, table] of Object.entries(tables)) {
    if (getTableConfig(table).name === targetSqlName) return exportName;
  }
  throw new Error(`Could not find export name for table "${targetSqlName}"`);
}

function sqlNameToExportName(tables: Record<string, PgTable>, sqlName: string): string {
  for (const [exportName, table] of Object.entries(tables)) {
    if (getTableConfig(table).name === sqlName) return exportName;
  }
  throw new Error(`No table export found with SQL name "${sqlName}"`);
}

function findDerivedNameForDescriptor(
  derived: Record<string, DerivedModelDescriptor>,
  descriptor: DerivedModelDescriptor,
): string {
  for (const [name, desc] of Object.entries(derived)) {
    if (desc === descriptor) return name;
  }
  throw new Error(
    `Could not find derived model name for descriptor with base "${descriptor.baseName}"`,
  );
}

function convertSearchFields(
  tables: Record<string, PgTable>,
  fields: SearchField[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const field of fields) {
    if ("name" in field && typeof (field as PgColumn).name === "string") {
      const resolved = resolveColumn(tables, field as PgColumn);
      if (!resolved) throw new Error(`Could not resolve column "${(field as PgColumn).name}"`);
      result[resolved.jsKey] = true;
    } else {
      for (const [relation, nested] of Object.entries(field)) {
        result[relation] = convertSearchFields(tables, nested as SearchField[]);
      }
    }
  }

  return result;
}

function convertSearchProfile(
  tables: Record<string, PgTable>,
  _modelTable: PgTable,
  profile: SearchProfile,
): { fields: Record<string, unknown> } {
  return { fields: convertSearchFields(tables, profile.fields) };
}

/**
 * Transform a ZenConfig into the resolved shapes consumed by codegen emit functions.
 * This replaces adaptToLegacyConfig by reading directly from ZenConfig instead of ModelsOutput.
 */
export function resolveConfig(config: ZenConfig): ResolvedConfig {
  const { tables, derived, models } = config;
  const globalDefaultWhere = config.default?.where;

  const computedFields: ComputedFieldsConfig = {};
  const derivedModels: DerivedModelsConfig = {};
  const filteredRelations: FilteredRelationsConfig = {};
  const searchDefaults: SearchDefaultsConfig = {};
  const throughRelations: ThroughRelationsConfig = {};
  const relationTargets: ResolvedRelationTargets = {};
  const modelScopes: Record<string, Record<string, unknown>> = {};
  const derivedFieldsMap: Record<string, Record<string, DerivedField>> = {};
  const validationSchemas: Record<string, Record<string, unknown>> = {};
  const fkRelations: Record<string, Record<string, FKRelationInfo>> = {};
  const brandedFields: Record<string, Record<string, true>> = {};
  const polymorphics: Record<string, ResolvedPolymorphic> = {};

  // Process derived model descriptors
  for (const [derivedName, desc] of Object.entries(derived)) {
    const baseExportName = sqlNameToExportName(tables, desc.baseName);
    if (!derivedModels[baseExportName]) {
      derivedModels[baseExportName] = {};
    }
    (derivedModels as any)[baseExportName][derivedName] = {
      where: desc.where,
      ...(desc.narrow ? { narrow: desc.narrow } : {}),
    };
  }

  // Process each model config
  for (const [modelName, modelConfig] of Object.entries(models)) {
    // Computed fields
    if (modelConfig.computed && typeof modelConfig.computed !== "function") {
      const resolvedComputed: Record<string, { type: string; tsType: string }> = {};

      for (const [fieldName, decl] of Object.entries(modelConfig.computed)) {
        if (typeof decl === "function") {
          resolvedComputed[fieldName] = COMPUTED_TYPE_MAP["json"]!;
        } else {
          const declObj = decl as { type?: string };
          const typeKey = declObj.type?.toLowerCase() ?? "json";
          resolvedComputed[fieldName] = COMPUTED_TYPE_MAP[typeKey] ?? COMPUTED_TYPE_MAP["json"]!;
        }
      }

      computedFields[modelName] = resolvedComputed;
    }

    // Relations
    if (modelConfig.relations) {
      for (const [relName, rel] of Object.entries(modelConfig.relations)) {
        if (rel.kind === "one") {
          const oneRel = rel as OneRelation;

          const targetTable =
            "__brand" in oneRel.target
              ? (oneRel.target as DerivedModelDescriptor).base
              : (oneRel.target as PgTable);
          const targetExportName = findExportNameForTable(tables, targetTable);
          const resolvedFields = oneRel.fields.map((col) => {
            const resolved = resolveColumn(tables, col);
            return resolved?.jsKey ?? col.name;
          });
          const resolvedRefs = oneRel.references.map((col) => {
            const resolved = resolveColumn(tables, col);
            return resolved?.jsKey ?? col.name;
          });

          if (oneRel.where) {
            // Polymorphic one() with where
            if (!fkRelations[modelName]) {
              fkRelations[modelName] = {};
            }
            fkRelations[modelName][relName] = {
              targetExportName,
              fields: resolvedFields,
              references: resolvedRefs,
              relationName: oneRel.relationName ?? null,
            };
            if (!filteredRelations[modelName]) {
              (filteredRelations as any)[modelName] = {};
            }
            (filteredRelations as any)[modelName][relName] = {
              relation: { [relName]: true },
              where: oneRel.where,
              single: true,
            };
          } else {
            // Regular FK relation
            if (!fkRelations[modelName]) {
              fkRelations[modelName] = {};
            }
            fkRelations[modelName][relName] = {
              targetExportName,
              fields: resolvedFields,
              references: resolvedRefs,
              relationName: oneRel.relationName ?? null,
            };
          }

          if (
            "__brand" in oneRel.target &&
            (oneRel.target as DerivedModelDescriptor).__brand === "derived"
          ) {
            const descriptor = oneRel.target as DerivedModelDescriptor;
            const derivedName = findDerivedNameForDescriptor(derived, descriptor);
            const baseExportName = sqlNameToExportName(tables, descriptor.baseName);

            if (!derivedModels[baseExportName]) {
              derivedModels[baseExportName] = {};
            }
            const derivedEntry = (derivedModels as any)[baseExportName][derivedName];
            if (derivedEntry) {
              if (!derivedEntry.relations) {
                derivedEntry.relations = {};
              }
              if (!derivedEntry.relations[modelName]) {
                derivedEntry.relations[modelName] = {};
              }
              derivedEntry.relations[modelName][relName] = true;
            }

            if (!relationTargets[modelName]) {
              relationTargets[modelName] = {};
            }
            relationTargets[modelName][relName] = derivedName;
          }
        } else if (rel.kind === "many") {
          const manyRel = rel as ManyRelation;
          const targetExportName = findExportNameForTable(tables, manyRel.target);

          if (!filteredRelations[modelName]) {
            (filteredRelations as any)[modelName] = {};
          }
          (filteredRelations as any)[modelName][relName] = {
            relation: { [targetExportName]: true },
            ...(manyRel.where ? { where: manyRel.where } : {}),
            ...(manyRel.single ? { single: true } : {}),
          };
        } else if (rel.kind === "through") {
          const throughRel = rel as ThroughRelation;

          if (!throughRelations[modelName]) {
            (throughRelations as any)[modelName] = {};
          }
          (throughRelations as any)[modelName][relName] = {
            path: throughRel.path,
          };
        }
      }
    }

    // Model scopes (defaultWhere)
    if (modelConfig.defaultWhere) {
      modelScopes[modelName] = modelConfig.defaultWhere;
    } else if (globalDefaultWhere) {
      modelScopes[modelName] = globalDefaultWhere;
    }

    // Derived fields
    if (modelConfig.derived) {
      derivedFieldsMap[modelName] = modelConfig.derived;
    }

    // Validation schemas
    if (modelConfig.validate) {
      validationSchemas[modelName] = modelConfig.validate;
    }

    // Branded fields
    if (modelConfig.branded) {
      brandedFields[modelName] = modelConfig.branded;
    }

    // Polymorphic resource_type/resource_id
    if (modelConfig.polymorphic) {
      const poly = modelConfig.polymorphic;
      const discResolved = resolveColumn(tables, poly.discriminator);
      const idResolved = resolveColumn(tables, poly.idField);
      if (!discResolved) throw new Error(`Could not resolve polymorphic discriminator column for model "${modelName}"`);
      if (!idResolved) throw new Error(`Could not resolve polymorphic idField column for model "${modelName}"`);

      const targets: ResolvedPolymorphicTarget[] = [];
      for (const [enumValue, targetCol] of Object.entries(poly.mapping)) {
        const targetResolved = resolveColumn(tables, targetCol);
        if (!targetResolved) throw new Error(`Could not resolve polymorphic target column for "${enumValue}" in model "${modelName}"`);
        const targetExportName = findExportNameForTable(tables, targetResolved.table);
        targets.push({
          table: enumValue,
          tableId: `${targetExportName}.${targetResolved.jsKey}`,
        });
      }

      const idColumn = poly.idField as any;
      const nullable = !idColumn.notNull;

      polymorphics[modelName] = {
        discriminator: discResolved.jsKey,
        idField: idResolved.jsKey,
        nullable,
        targets,
      };
    }

    // Search profiles
    if (modelConfig.search) {
      const modelSearchDefaults: Record<string, any> = {};
      for (const [profileName, profile] of Object.entries(modelConfig.search)) {
        modelSearchDefaults[profileName] = convertSearchProfile(
          tables,
          modelConfig.table,
          profile,
        );
      }
      searchDefaults[modelName] = modelSearchDefaults;
    }
  }

  return {
    defaultWhere: globalDefaultWhere,
    computedFields,
    derivedModels,
    filteredRelations,
    searchDefaults,
    throughRelations,
    relationTargets,
    modelScopes,
    derivedFields: derivedFieldsMap,
    validationSchemas,
    fkRelations,
    brandedFields,
    polymorphics,
  };
}
